/**
 * @fileoverview Audit Log date-range helpers.
 * @description The page filters by local calendar dates, but GET /audit/events wants inclusive ISO
 * 8601 UTC instants and refuses a range that is inverted or longer than AUDIT_QUERY_MAX_RANGE_DAYS.
 * These helpers build the default last-7-days range, check a range the way the backend will, and
 * turn the page's filter state into request parameters (a date's start and end of day, in the
 * viewer's own time zone, converted to UTC).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  AUDIT_DEFAULT_RANGE_DAYS,
  AUDIT_EVENT_TYPE_PATTERN,
  AUDIT_MAX_RANGE_DAYS,
} from '@/features/audit/constants/audit-filters.constants'

import type { AuditEventsParams, AuditFilters } from '@/features/audit/types/audit.types'

export type AuditRangeProblem = 'missing' | 'inverted' | 'tooLong'

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * @description Formats a Date as a local calendar date, the value a date input holds.
 * @param {Date} date - The date.
 * @returns {string} YYYY-MM-DD, in the viewer's time zone.
 */
export function toLocalDateString(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * @description Builds the filters the page opens with: the last seven days, nothing else set.
 * @param {Date} [now] - The current time (injectable for tests).
 * @returns {AuditFilters} The default filters.
 */
export function defaultAuditFilters(now: Date = new Date()): AuditFilters {
  const start = new Date(now)
  start.setDate(start.getDate() - (AUDIT_DEFAULT_RANGE_DAYS - 1))

  return {
    fromDate: toLocalDateString(start),
    toDate: toLocalDateString(now),
    result: '',
    actorType: '',
    eventType: '',
    sourceModule: '',
  }
}

/**
 * @description Reads a YYYY-MM-DD date as a moment in the viewer's time zone.
 * @param {string} value - The calendar date.
 * @param {boolean} endOfDay - True for 23:59:59.999, false for 00:00:00.000.
 * @returns {Date | null} The moment, or null when the value is not a real date.
 */
function parseLocalDate(value: string, endOfDay: boolean): Date | null {
  const match = DATE_PATTERN.exec(value)
  if (!match) return null

  const [, year, month, day] = match
  const date = endOfDay
    ? new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999)
    : new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0)

  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * @description Checks a date range the way the backend will before it is sent.
 * @param {string} fromDate - The first day, YYYY-MM-DD.
 * @param {string} toDate - The last day, YYYY-MM-DD.
 * @returns {AuditRangeProblem | null} What is wrong with the range, or null when it is searchable.
 */
export function checkAuditRange(fromDate: string, toDate: string): AuditRangeProblem | null {
  const from = parseLocalDate(fromDate, false)
  const to = parseLocalDate(toDate, true)

  if (!from || !to) return 'missing'
  if (from.getTime() > to.getTime()) return 'inverted'
  if ((to.getTime() - from.getTime()) / MS_PER_DAY > AUDIT_MAX_RANGE_DAYS) return 'tooLong'
  return null
}

/**
 * @description Whether an event-type filter is empty or one the backend will accept.
 * @param {string} eventType - The text typed into the filter.
 * @returns {boolean} True when it can be sent (or is blank, meaning no filter).
 */
export function isValidEventTypeFilter(eventType: string): boolean {
  const trimmed = eventType.trim()
  return trimmed === '' || AUDIT_EVENT_TYPE_PATTERN.test(trimmed)
}

/**
 * @description Turns the page's filter state into GET /audit/events parameters. Blank filters are
 * left out, and an event type the backend would refuse is not sent.
 * @param {AuditFilters} filters - The page's filters.
 * @param {number} page - The 1-based page.
 * @param {number} limit - The page size.
 * @returns {AuditEventsParams | null} The parameters, or null when the date range is not searchable.
 */
export function toAuditParams(
  filters: AuditFilters,
  page: number,
  limit: number,
): AuditEventsParams | null {
  const from = parseLocalDate(filters.fromDate, false)
  const to = parseLocalDate(filters.toDate, true)
  if (!from || !to || checkAuditRange(filters.fromDate, filters.toDate) !== null) return null

  const eventType = filters.eventType.trim()

  return {
    from: from.toISOString(),
    to: to.toISOString(),
    sortDirection: 'DESC',
    page,
    limit,
    ...(filters.result && { result: filters.result }),
    ...(filters.actorType && { actorType: filters.actorType }),
    ...(filters.sourceModule && { sourceModule: filters.sourceModule }),
    ...(eventType && isValidEventTypeFilter(eventType) && { eventType }),
  }
}
